/* eslint-disable */

import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Avatar,
  Badge,
  ListItemAvatar,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  Tooltip,
  useTheme,
} from '@mui/material';

import {
  PushPin,
  PushPinOutlined,
  Cancel,
  CheckCircle,
  Replay,
  Reply,
  SwapHorizontalCircle,
} from '@mui/icons-material';

import ButtonWithSpinner from '../ButtonWithSpinner';
import MarkdownWrapper from '../MarkdownWrapper';
import ConnectionIcon from '../ConnectionIcon';
import TransferTicketModalCustom from '../TransferTicketModalCustom';
import ShowTicketOpen from '../ShowTicketOpenModal';

import { parseISO } from 'date-fns';
import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import { TicketsContext } from '../../context/Tickets/TicketsContext';
import toastError from '../../toasts/toastError';
import { v4 as uuidv4 } from 'uuid';
import { isNil } from 'lodash';
import useCompanySettings from '../../hooks/useSettings/companySettings';
import { getStyled } from './styles';

const TicketListItemCustom = ({ setAlertOpen, ticket, fixedList }) => {
  const theme = useTheme();
  const open = ticket.status === 'open';
  const classes = getStyled(theme, open);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState('');
  const [queueTicketOpen, setQueueTicketOpen] = useState('');
  const [groupUnread, setGroupUnread] = useState(null);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const { ticketId } = useParams();
  const { setCurrentTicket, setTab, setTabOpen } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);

  const { get: getSetting } = useCompanySettings();

  useEffect(() => {
    const checkUnreadMessages = async () => {
      if (ticket.status === 'open' && ticket.unreadMessages > 0) {
        setAlertOpen(true);
      }

      if (ticket.isGroup) {
        try {
          const companyId = ticket.contact.companyId;
          const ticketId = ticket.id;
          const { data: unreadMessage } = await api.post(
            '/message/count/unread',
            {
              companyId,
              ticketId,
            },
          );
          if (isMounted.current) setGroupUnread(unreadMessage);
        } catch (error) {
          console.error('Error fetching unread messages:', error);
        }
      }
    };

    checkUnreadMessages();
  }, [
    ticket.unreadMessages,
    ticket.status,
    ticket.isGroup,
    ticket.contact?.companyId,
    ticket.id,
    ticket.updatedAt,
  ]);

  const handleFixTicket = async (ticketId) => {
    try {
      if (fixedList.includes(ticketId)) {
        await api.put(`/tickets/${ticketId}`, { pinnedTicket: false });
      } else {
        await api.put(`/tickets/${ticketId}`, { pinnedTicket: true });
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleCloseIgnoreTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: 'canceled',
        userId: user?.id || null,
        sendFarewellMessage: false,
        amountUsedBotQueues: 0,
      });

      await api.post(`/messages/${ticket.id}`, {
        isPrivate: 'true',
        body: `❌ Ticket cancelado por: *${user.name}*`,
        read: true,
        fromMe: true,
      });

      if (isMounted.current) setLoading(false);
      navigate(`/tickets/`);
    } catch (err) {
      if (isMounted.current) setLoading(false);
      toastError(err);
    }
  };

  const truncate = (str, len) => {
    if (!isNil(str)) {
      if (str.length > len) {
        return str.substring(0, len) + '...';
      }
      return str;
    }
  };

  const handleCloseTransferTicketModal = useCallback(() => {
    setTransferTicketModalOpen(false);
  }, []);

  const handleOpenTransferModal = () => {
    setTransferTicketModalOpen(true);
    setLoading(false);
    handleSelectTicket(ticket);
    navigate(`/tickets/${ticket.uuid}`);
  };

  const handleNewTicket = async (ticket) => {
    try {
      setLoading(true);
      const data = {
        contactId: ticket.contactId,
        status: 'open',
        userId: user.id,
        queueId: ticket.queueId ? ticket.queueId : user.queues[0].id,
        whatsappId: ticket.whatsappId,
      };

      const { data: newTicket } = await api.post(`/tickets`, data);

      let setting;
      try {
        setting = await getSetting({
          column: 'sendGreetingAccepted',
        });
      } catch (err) {
        if (isMounted.current) {
          toastError(err);
        }
      }

      if (
        setting?.sendGreetingAccepted === 'enabled' &&
        (!newTicket.isGroup || newTicket.whatsapp?.groupAsTicket === 'enabled')
      ) {
        await handleSendMessage(newTicket.id);
      }

      setTab(newTicket.isGroup ? 'group' : 'open');
      setTabOpen(newTicket.isGroup ? 'group' : 'open');
      handleSelectTicket(newTicket);
      navigate(`/tickets/${newTicket.uuid}`);
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleAcepptTicket = async (ticket) => {
    setLoading(true);

    try {
      const otherTicket = await api.put(`/tickets/${ticket.id}`, {
        status:
          ticket.isGroup && ticket.channel === 'whatsapp' ? 'group' : 'open',
        userId: user?.id,
        queueId: ticket.queueId ? ticket.queueId : user.queues[0].id,
      });

      if (otherTicket.data.id !== ticket.id) {
        if (otherTicket.data.userId !== user?.id) {
          if (isMounted.current) {
            setOpenAlert(true);
            setUserTicketOpen(otherTicket.data.user.name);
            setQueueTicketOpen(otherTicket.data.queue.name);
          }
        } else {
          if (isMounted.current) setLoading(false);
          setTab(ticket.isGroup ? 'group' : 'open');
          setTabOpen(ticket.isGroup ? 'group' : 'open');
          handleSelectTicket(otherTicket.data);
          navigate(`/tickets/${otherTicket.data.uuid}`);
        }
      } else {
        let setting;

        try {
          setting = await getSetting({
            column: 'sendGreetingAccepted',
          });
        } catch (err) {
          toastError(err);
        }

        if (
          setting?.sendGreetingAccepted === 'enabled' &&
          (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === 'enabled')
        ) {
          await handleSendMessage(ticket.id);
        }

        setTab(ticket.isGroup ? 'group' : 'open');
        setTabOpen(ticket.isGroup ? 'group' : 'open');
        handleSelectTicket(ticket);
        navigate(`/tickets/${ticket.uuid}`);
        if (isMounted.current) setLoading(false);
      }
    } catch (err) {
      if (isMounted.current) setLoading(false);
      toastError(err);
    }
  };

  const handleSendMessage = async (id, farewellMsg) => {
    if (!farewellMsg || farewellMsg.trim() === '') return;

    try {
      const message = {
        read: 1,
        fromMe: true,
        mediaUrl: '',
        body: farewellMsg.trim(),
      };

      await api.post(`/messages/${id}`, message);
    } catch (err) {
      if (isMounted.current) {
        toastError(err);
      }
    }
  };

  const handleResolveTicket = async (ticket) => {
    setLoading(true);

    try {
      await api.put(`/tickets/${ticket.id}`, {
        status: 'closed',
        userId: user.id,
        queueId: ticket.queueId,
        whatsappId: ticket.whatsappId,
        sendFarewellMessage: true,
      });

      await api.post(`/messages/${ticket.id}`, {
        isPrivate: 'true',
        body: `✔️ Ticket finalizado por: *${user.name}*`,
        read: true,
        fromMe: true,
      });

      if (isMounted.current) {
        setLoading(false);
      }
    } catch (err) {
      if (isMounted.current) {
        setLoading(false);
        toastError(err);
      }
    }
  };

  const handleCloseAlert = useCallback(() => {
    if (isMounted.current) {
      setOpenAlert(false);
      setLoading(false);
    }
  }, []);

  const handleSelectTicket = (ticket) => {
    const { uuid } = ticket;

    navigate(`/tickets/${uuid}`);
  };

  const getInteractionTimeLabel = () => {
    if (!ticket.lastMessage) return null;

    const lastInteractionDate = parseISO(ticket.updatedAt);
    const currentDate = new Date();
    const timeDifference = currentDate - lastInteractionDate;
    const minutesDifference = Math.floor(timeDifference / (1000 * 60));
    const hoursDifference = Math.floor(timeDifference / (1000 * 60 * 60));

    if (minutesDifference < 1) return null;

    let labelText = '';
    let styleName = '';

    if (minutesDifference < 60) {
      labelText = `${minutesDifference}m`;
      styleName = 'recent';
    } else if (hoursDifference < 24) {
      labelText = `${hoursDifference}h`;
      styleName = hoursDifference > 1 ? 'warning' : 'recent';
    } else {
      labelText = `${Math.floor(hoursDifference / 24)}d`;
      styleName = 'critical';
    }

    return (
      <span style={{ ...classes.interactionTime, ...classes[styleName] }}>
        {labelText}
      </span>
    );
  };

  return (
    <React.Fragment key={ticket.id}>
      {openAlert && (
        <ShowTicketOpen
          isOpen={openAlert}
          handleClose={handleCloseAlert}
          user={userTicketOpen}
          queue={queueTicketOpen}
        />
      )}
      {transferTicketModalOpen && (
        <TransferTicketModalCustom
          modalOpen={transferTicketModalOpen}
          onClose={handleCloseTransferTicketModal}
          ticketid={ticket.id}
          ticket={ticket}
        />
      )}
      <ListItemButton
        dense
        onClick={(e) => {
          const isCheckboxClicked =
            (e.target.tagName.toLowerCase() === 'input' &&
              e.target.type === 'checkbox') ||
            (e.target.tagName.toLowerCase() === 'svg' &&
              e.target.type === undefined) ||
            (e.target.tagName.toLowerCase() === 'path' &&
              e.target.type === undefined);

          if (isCheckboxClicked) return;

          handleSelectTicket(ticket);
        }}
        selected={ticketId && ticketId === ticket.uuid}
        sx={{
          ...classes.ticket,
          ...(ticket.status === 'pending' && classes.pendingTicket),
        }}
      >
        <ListItemAvatar style={classes.itemAvatar}>
          <>
            {ticket.status === 'open' && (
              <ButtonWithSpinner
                style={classes.fixActionBtn}
                size='small'
                loading={loading}
                onClick={(e) => handleFixTicket(ticket.id)}
              >
                <Tooltip
                  title={
                    fixedList.includes(ticket.id)
                      ? i18n.t('ticketsList.buttons.unfix')
                      : i18n.t('ticketsList.buttons.fix')
                  }
                >
                  {fixedList.includes(ticket.id) ? (
                    <PushPin
                      style={{ ...classes.ticketIcon, ...classes.pinIcon }}
                    />
                  ) : (
                    <PushPinOutlined
                      style={{ ...classes.ticketIcon, ...classes.pinIcon }}
                    />
                  )}
                </Tooltip>
              </ButtonWithSpinner>
            )}
            <Avatar
              style={classes.avatar}
              src={`${ticket?.contact?.urlPicture}`}
            />
          </>
        </ListItemAvatar>
        <ListItemText
          sx={{
            marginLeft: '-12px',
          }}
          disableTypography
          primary={
            <span style={classes.contactNameWrapper}>
              <Typography
                noWrap
                component='span'
                variant='body2'
                sx={classes.responsiveText}
              >
                &nbsp;
                {ticket.channel && (
                  <ConnectionIcon connectionType={ticket.channel} />
                )}
                &nbsp;
                {truncate(ticket.contact?.name, 60)} &nbsp;
                {getInteractionTimeLabel()}
              </Typography>

              <Badge
                sx={{ ...classes.newMessagesCount, ...classes.badgeStyle }}
                badgeContent={groupUnread || ticket.unreadMessages}
              />
            </span>
          }
          secondary={
            <>
              <span style={classes.contactNameWrapper}>
                <Typography
                  sx={{
                    ...(Number(ticket.unreadMessages) > 0
                      ? classes.contactLastMessageUnread
                      : classes.contactLastMessage),
                    margin: '-3px 0 0 5px',
                  }}
                  noWrap
                  component='span'
                  variant='body2'
                >
                  {ticket.lastMessage ? (
                    <span
                      style={{
                        width: 'max-width',
                        fontSize: '12px',
                        fontWeight: 'lighter',
                        overflow: 'hidden',
                      }}
                    >
                      {ticket.lastMessage.includes('fb.me') ? (
                        <MarkdownWrapper>Clique de Anúncio</MarkdownWrapper>
                      ) : ticket.lastMessage.includes(
                          'data:image/png;base64',
                        ) ? (
                        <MarkdownWrapper>Localização</MarkdownWrapper>
                      ) : (
                        <>
                          {' '}
                          {ticket.lastMessage.includes('BEGIN:VCARD') ? (
                            <MarkdownWrapper>Contato</MarkdownWrapper>
                          ) : (
                            <MarkdownWrapper>
                              {truncate(ticket.lastMessage, 40)}
                            </MarkdownWrapper>
                          )}
                        </>
                      )}
                    </span>
                  ) : (
                    <span style={{ visibility: 'hidden' }}> </span>
                  )}
                  {
                    <span
                      style={{
                        ...classes.secondaryContentSecond,
                        marginTop: '3px',
                        marginLeft: '1px',
                        overflow: 'visible',
                      }}
                    >
                      {ticket?.whatsapp && (
                        <Badge
                          sx={{
                            ...classes.connectionTag,
                            backgroundColor:
                              ticket.channel === 'whatsapp'
                                ? '#086d2d'
                                : ticket.channel === 'facebook'
                                  ? '#4267B2'
                                  : '#E1306C',
                          }}
                        >
                          {ticket.whatsapp?.name.toUpperCase()}
                        </Badge>
                      )}
                      {ticket.isGroup &&
                      ticket.whatsapp.groupAsTicket ? null : (
                        <Badge
                          sx={{
                            ...classes.connectionTag,
                            backgroundColor: ticket.queue?.color || '#7c7c7c',
                          }}
                        >
                          {ticket.queueId
                            ? ticket.queue?.name.toUpperCase()
                            : ticket.status === 'lgpd'
                              ? 'LGPD'
                              : 'SEM FILA'}
                        </Badge>
                      )}
                    </span>
                  }
                  {ticket?.user && (
                    <span
                      style={{
                        ...classes.secondaryContentSecond,
                        marginTop: '3px',
                      }}
                    >
                      <Badge
                        sx={{
                          ...classes.connectionTag,
                          backgroundColor: '#181c35',
                        }}
                      >
                        {ticket.user?.name.toUpperCase()}
                        {ticket.status === 'group' && user.name.toUpperCase()}
                      </Badge>
                    </span>
                  )}
                  {ticket?.isGroup && (
                    <span
                      style={{
                        ...classes.secondaryContentSecond,
                        marginTop: '3px',
                      }}
                    >
                      <Badge
                        sx={{
                          ...classes.connectionTag,
                          backgroundColor: '#181c35',
                        }}
                      >
                        {user?.name.toUpperCase()}
                      </Badge>
                    </span>
                  )}
                </Typography>
              </span>
              {
                <ListItemSecondaryAction
                  style={{
                    position: 'absolute',
                    right: '-0.55rem',
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    minWidth: 'fit-content',
                    maxHeight: 'fit-content',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {ticket.status === 'pending' &&
                    (ticket.queueId === null ||
                      ticket.queueId === undefined) && (
                      <ButtonWithSpinner
                        style={{
                          ...classes.ticketIconOpen,
                          ...classes.actionBtn,
                        }}
                        size='small'
                        loading={loading}
                        onClick={(e) => handleAcepptTicket(ticket)}
                      >
                        <Tooltip
                          title={`${i18n.t('ticketsList.buttons.accept')}`}
                        >
                          <Reply style={classes.ticketIcon} />
                        </Tooltip>
                      </ButtonWithSpinner>
                    )}
                  {ticket.status === 'pending' && ticket.queueId !== null && (
                    <ButtonWithSpinner
                      style={{
                        ...classes.ticketIconOpen,
                        ...classes.actionBtn,
                      }}
                      size='small'
                      loading={loading}
                      onClick={(e) => handleAcepptTicket(ticket)}
                    >
                      <Tooltip
                        title={`${i18n.t('ticketsList.buttons.accept')}`}
                      >
                        <Reply style={classes.ticketIcon} />
                      </Tooltip>
                    </ButtonWithSpinner>
                  )}
                  {(ticket.status === 'open' || ticket.status === 'group') && (
                    <ButtonWithSpinner
                      style={{
                        ...classes.ticketIconClose,
                        ...classes.actionBtn,
                      }}
                      size='small'
                      loading={loading}
                      onClick={(e) => handleResolveTicket(ticket)}
                    >
                      <Tooltip
                        title={`${i18n.t('ticketsList.buttons.closed')}`}
                      >
                        <CheckCircle style={classes.ticketIcon} />
                      </Tooltip>
                    </ButtonWithSpinner>
                  )}
                  {(ticket.status === 'pending' ||
                    ticket.status === 'open') && (
                    <ButtonWithSpinner
                      style={{
                        ...classes.ticketIconTransfer,
                        ...classes.actionBtn,
                      }}
                      size='small'
                      loading={loading}
                      onClick={handleOpenTransferModal}
                    >
                      <Tooltip
                        title={`${i18n.t('ticketsList.buttons.transfer')}`}
                      >
                        <SwapHorizontalCircle style={classes.ticketIcon} />
                      </Tooltip>
                    </ButtonWithSpinner>
                  )}
                  {(ticket.status === 'open' || ticket.status === 'group') && (
                    <ButtonWithSpinner
                      style={{
                        ...classes.ticketIconCancel,
                        ...classes.actionBtn,
                      }}
                      size='small'
                      loading={loading}
                      onClick={(e) => handleCloseIgnoreTicket(ticket.id)}
                    >
                      <Tooltip
                        title={`${i18n.t('ticketsList.buttons.cancel')}`}
                      >
                        <Cancel style={classes.ticketIcon} />
                      </Tooltip>
                    </ButtonWithSpinner>
                  )}
                  {(ticket.status === 'pending' || ticket.status === 'lgpd') &&
                    (user.userClosePendingTicket === 'enabled' ||
                      user.profile === 'admin') && (
                      <ButtonWithSpinner
                        style={{
                          ...classes.ticketIconCancel,
                          ...classes.actionBtn,
                        }}
                        size='small'
                        loading={loading}
                        onClick={(e) => handleCloseIgnoreTicket(ticket.id)}
                      >
                        <Tooltip
                          title={`${i18n.t('ticketsList.buttons.cancel')}`}
                        >
                          <Cancel style={classes.ticketIcon} />
                        </Tooltip>
                      </ButtonWithSpinner>
                    )}
                  {(ticket.status === 'closed' ||
                    ticket.status === 'canceled') &&
                    (ticket.queueId === null ||
                      ticket.queueId === undefined) && (
                      <ButtonWithSpinner
                        sx={{
                          ...classes.ticketIconReopen,
                          ...classes.actionBtn,
                        }}
                        size='small'
                        loading={loading}
                        onClick={(e) => handleNewTicket(ticket)}
                      >
                        <Tooltip
                          title={`${i18n.t('ticketsList.buttons.reopen')}`}
                        >
                          <Replay style={classes.ticketIcon} />
                        </Tooltip>
                      </ButtonWithSpinner>
                    )}
                  {(ticket.status === 'closed' ||
                    ticket.status === 'canceled') &&
                    ticket.queueId !== null && (
                      <ButtonWithSpinner
                        style={{
                          ...classes.ticketIconReopen,
                          ...classes.actionBtn,
                        }}
                        size='small'
                        loading={loading}
                        onClick={(e) => handleNewTicket(ticket)}
                      >
                        <Tooltip
                          title={`${i18n.t('ticketsList.buttons.reopen')}`}
                        >
                          <Replay style={classes.ticketIcon} />
                        </Tooltip>
                      </ButtonWithSpinner>
                    )}
                </ListItemSecondaryAction>
              }
            </>
          }
        />
      </ListItemButton>
    </React.Fragment>
  );
};

export default TicketListItemCustom;
